import { useSendCommand } from '@/_core/cqrs/use';
import { RealTimeAction } from '@/_core/fluxstore/types';
import { StorymapState, Subtask, SubtaskStatusCategory } from '../../types';
import { getCardById, isActivity, isSubtask, isTask } from '../../use';
import { createMoveCardToSubTaskCommand } from '../../../api/commands/factory';
import { useStorymapStore } from '../../useModule';
import { FlashJustDragged } from './flashJustDragged';
import { SetActiveCardIdByClick } from '../cardSelection/setActiveCardIdByClick';
import { snackbar } from '@/_core/app-components/use';
import { createSubtaskFromActivity, createSubtaskFromTask } from '../../use/mapCards';
import { HandleMoveCardResult } from './handleMoveCardResult';
import orderBy from 'lodash/orderBy';

const { registerReducer, registerMiddleware, dispatch } = useStorymapStore();

export class MoveCardToSubtask extends RealTimeAction<StorymapState> {
    public static ACTION_TYPE = 'MoveSubtaskCard';

    public constructor(public id: string, public releaseId: string, public taskId: string, public newOrder: number = null) {
        super(MoveCardToSubtask.ACTION_TYPE);
    }
}

const canMoveIntoMiddleware = async (state: StorymapState, action: MoveCardToSubtask): Promise<boolean> => {
    const card = getCardById(action.id);
    const destinationTask = state.tasks.find((x) => x.id === action.taskId);

    let hasChild = false;
    let moveToSelf = false;

    if (isActivity(card)) {
        hasChild = state.tasks.some((x) => x.activityId === card.id);
        moveToSelf = card.id === destinationTask.activityId;
    }

    if (isTask(card)) {
        hasChild = state.subtasks.some((x) => x.taskId === card.id);
        moveToSelf = card.id === destinationTask.id;
    }

    if (moveToSelf) {
        snackbar.showWarning("Can't move into itself");
        return false;
    }

    if (hasChild) {
        snackbar.showWarning("Can't move there because it has child cards");
        return false;
    }

    return true;
};

const setOrderMiddleWare = async (state: StorymapState, action: MoveCardToSubtask): Promise<boolean> => {
    if (action.newOrder === null) {
        action.newOrder = 0;

        const otherCards = state.subtasks.filter((x) => x.releaseId == action.releaseId && x.taskId == action.taskId);
        if (otherCards.length > 0) {
            const maxOrder = Math.max(...otherCards.map((x) => x.order));
            action.newOrder = maxOrder + 1;
        }
    }

    return true;
};

const middleware = async (state: StorymapState, action: MoveCardToSubtask): Promise<boolean> => {
    (async () => {
        const cmd = await createMoveCardToSubTaskCommand(action);
        useSendCommand(action, {
            createCommand: () => cmd,
            onSuccess: (res) => {
                if (res.PushedActivity != null || res.PushedTask != null || (res.UnlinkedCardIds != null && res.UnlinkedCardIds.length > 0)) {
                    dispatch(new HandleMoveCardResult(res));
                }
            },
        });
    })();

    return true;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const reducer = (state: StorymapState, action: MoveCardToSubtask): void => {
    const card = getCardById(action.id);
    const destinationTask = state.tasks.find((x) => x.id === action.taskId);
    const defaultStatus = orderBy(
        state.subtaskStatuses.filter((x) => x.category === SubtaskStatusCategory.Todo),
        (x) => x.order
    )[0].key;

    let subtask: Subtask = null;
    if (isSubtask(card)) {
        subtask = card as Subtask;

        state.subtasks
            .filter((x) => x.taskId === subtask.taskId && x.releaseId === subtask.releaseId && x.order > subtask.order)
            .forEach((x) => x.order--);

        state.subtasks
            .filter((x) => x.taskId === action.taskId && x.releaseId === action.releaseId && x.order >= action.newOrder)
            .forEach((x) => x.order++);

        subtask.activityId = destinationTask.activityId;
        subtask.taskId = action.taskId;
        subtask.releaseId = action.releaseId;
        subtask.order = action.newOrder;
    }

    if (isTask(card)) {
        state.tasks.filter((x) => x.activityId === card.activityId && x.order > card.order).forEach((x) => x.order--);

        state.tasks = state.tasks.filter((x) => x.id !== card.id);

        state.subtasks
            .filter((x) => x.taskId === action.taskId && x.releaseId === action.releaseId && x.order >= action.newOrder)
            .forEach((x) => x.order++);

        subtask = createSubtaskFromTask(card, destinationTask, action.releaseId, action.newOrder, state.taskColor, state.subtaskColor);
        subtask.status = defaultStatus;

        state.subtasks.push(subtask);
    }

    if (isActivity(card)) {
        state.activities.filter((x) => x.order > card.order).forEach((x) => x.order--);
        state.activities = state.activities.filter((x) => x.id !== card.id);

        state.subtasks
            .filter((x) => x.taskId === action.taskId && x.releaseId === action.releaseId && x.order >= action.newOrder)
            .forEach((x) => x.order++);

        subtask = createSubtaskFromActivity(
            card,
            destinationTask,
            action.releaseId,
            action.newOrder,
            state.activityColor,
            state.subtaskColor
        );
        subtask.status = defaultStatus;

        state.subtasks.push(subtask);
    }

    subtask.lastUpdatedAt = new Date();

    // Release view order
    const releaseViewSiblings = orderBy(
        state.subtasks.filter((x) => x.status == subtask.status && x.releaseId == subtask.releaseId && x.id != subtask.id),
        (x) => x.releaseViewOrder
    );
    
    // Check if ANY sibling has been initialized (non-null releaseViewOrder)
    const hasInitializedSiblings = releaseViewSiblings.some(x => x.releaseViewOrder !== null && x.releaseViewOrder !== undefined);
    
    let releaseViewOrder;
    if (hasInitializedSiblings) {
        // Status has been initialized - continue the sequence
        const nonNullOrders = releaseViewSiblings
            .filter(x => x.releaseViewOrder !== null && x.releaseViewOrder !== undefined)
            .map(x => x.releaseViewOrder);
        releaseViewOrder = Math.max(...nonNullOrders) + 1;
    } else {
        // Status has never been initialized - preserve null
        releaseViewOrder = null;
    }
    subtask.releaseViewOrder = releaseViewOrder;

    if (!action.isDispatchedByRealTimeCallback) {
        dispatch(new SetActiveCardIdByClick(card.id));
        dispatch(new FlashJustDragged());
    }
};

//@ts-ignore
registerMiddleware(MoveCardToSubtask.ACTION_TYPE, canMoveIntoMiddleware);
//@ts-ignore
registerMiddleware(MoveCardToSubtask.ACTION_TYPE, setOrderMiddleWare);
//@ts-ignore
registerMiddleware(MoveCardToSubtask.ACTION_TYPE, middleware);
//@ts-ignore
registerReducer(MoveCardToSubtask.ACTION_TYPE, reducer);
